import { useNavigate } from 'react-router-dom';
import {
  HiOutlineBuildingOffice,
  HiOutlineFolderOpen,
  HiOutlineCurrencyDollar,
  HiOutlineChartBarSquare,
} from 'react-icons/hi2';
import type { OrgStats } from '../../api/analytics';
import { StatCard } from '../shared/StatCard';
import { ChartCard } from '../charts/ChartCard';
import { CostDonutChart } from '../charts/CostDonutChart';
import { ComparisonBarChart } from '../charts/ComparisonBarChart';
import { DataTable } from '../shared/DataTable';
import type { Column } from '../shared/DataTable';
import { buildCostDonutData } from '../charts/chartTheme';
import { fmt, fmtCHF, fmtM } from '../../utils/format';
import styles from './OrgAnalyticsPage.module.css';

interface Props {
  data: OrgStats;
}

export function CantonView({ data }: Props) {
  const navigate = useNavigate();

  const donutData = buildCostDonutData(data.cost_breakdown);

  const communeBarData = data.children_stats.map((c) => ({
    name: c.org_name.length > 12 ? c.org_name.slice(0, 12) + '...' : c.org_name,
    logements: c.total_logements,
    emplois: c.total_emplois,
  }));

  const sbpData = [
    {
      name: data.organization.name,
      sbp_logements: data.sbp_breakdown.logements || 0,
      sbp_activites: data.sbp_breakdown.activites || 0,
      sbp_equipements: data.sbp_breakdown.equipements || 0,
    },
  ];

  const columns: Column<typeof data.children_stats[number]>[] = [
    { key: 'org_name', label: 'Commune' },
    { key: 'project_count', label: 'Projects', align: 'right', render: (r) => fmt(r.project_count) },
    { key: 'total_logements', label: 'Logements', align: 'right', render: (r) => fmt(r.total_logements) },
    { key: 'total_emplois', label: 'Emplois', align: 'right', render: (r) => fmt(r.total_emplois) },
    { key: 'total_ha', label: 'Ha', align: 'right', render: (r) => r.total_ha > 0 ? r.total_ha.toFixed(1) : '\u2014' },
    {
      key: 'density',
      label: 'Density',
      align: 'right',
      render: (r) => r.total_ha > 0 ? (r.total_logements / r.total_ha).toFixed(1) : '\u2014',
    },
  ];

  return (
    <>
      <div className={styles.statsRow}>
        <StatCard label="Communes" value={data.children_stats.length} icon={<HiOutlineBuildingOffice />} />
        <StatCard label="Total Projects" value={data.total_projects} icon={<HiOutlineFolderOpen />} />
        <StatCard
          label="Total Cost"
          value={data.total_cost > 0 ? fmtCHF(data.total_cost) : '\u2014'}
          icon={<HiOutlineCurrencyDollar />}
          color="var(--color-primary)"
        />
        <StatCard
          label="Avg Density"
          value={data.avg_density > 0 ? `${data.avg_density}` : '\u2014'}
          sub="logements/ha"
          icon={<HiOutlineChartBarSquare />}
        />
      </div>

      <div className={styles.chartsRow}>
        <ChartCard title="Communes Comparison" subtitle="Logements & Emplois">
          <ComparisonBarChart
            data={communeBarData}
            metrics={[
              { key: 'logements', label: 'Logements', color: '#6366f1' },
              { key: 'emplois', label: 'Emplois', color: '#22d3ee' },
            ]}
            layout="horizontal"
          />
        </ChartCard>
        <ChartCard title="Cost Breakdown" subtitle="Canton-level">
          <CostDonutChart
            data={donutData}
            centerValue={data.total_cost > 0 ? fmtM(data.total_cost) : undefined}
            centerLabel="Total"
          />
        </ChartCard>
      </div>

      {((data.sbp_breakdown.logements ?? 0) > 0 || (data.sbp_breakdown.activites ?? 0) > 0 || (data.sbp_breakdown.equipements ?? 0) > 0) && (
        <div className={styles.chartsRow}>
          <ChartCard title="SBP Composition" subtitle="Surface breakdown (m²)">
            <ComparisonBarChart
              data={sbpData}
              metrics={[
                { key: 'sbp_logements', label: 'Logements', color: '#6366f1' },
                { key: 'sbp_activites', label: 'Activités', color: '#22d3ee' },
                { key: 'sbp_equipements', label: 'Équipements', color: '#a78bfa' },
              ]}
              layout="horizontal"
              stacked
              formatValue={(v) => `${v.toLocaleString('fr-CH')} m²`}
            />
          </ChartCard>
          <div /> {/* Spacer for grid alignment */}
        </div>
      )}

      <div className={styles.tableSection}>
        <h3>Communes Details</h3>
        <DataTable
          columns={columns}
          data={data.children_stats}
          keyField="org_id"
          onRowClick={(child) => navigate(`/app/analytics/${child.org_id}`)}
        />
      </div>
    </>
  );
}
